/**
 * Module dependencies.
 */

import { ChangeEvent, ComponentProps, FormEventHandler } from 'react';
import { Control, FieldValues, Path, RegisterOptions, UseControllerProps, useController } from 'react-hook-form';
import { assignRefs } from 'src/core/utils/refs';

import { Input, InputProps } from './index';

/**
 * Export `InputFieldProps` interface.
 */

export type InputFieldProps<T extends FieldValues = FieldValues> = {
  control: Control<T>;
  defaultValue?: any;
  hideErrors?: boolean;
  name: string;
  onChangeNormalizer?: (
    e: ChangeEvent<HTMLInputElement>,
    onChange: ((event: ChangeEvent<HTMLInputElement>) => void) & FormEventHandler<HTMLInputElement>,
    value: InputProps['value']
  ) => void;
  ref?: ComponentProps<typeof Input>['ref'];
  rules?: RegisterOptions;
} & Omit<InputProps, 'hasValue' | 'id'>;

/**
 * Export `InputField` component.
 */

export function InputField<T extends FieldValues>(props: InputFieldProps<T>) {
  const {
    control,
    defaultValue = '',
    hideErrors,
    maxLength,
    name,
    onChangeNormalizer,
    ref: forwardedRef,
    rules,
    type,
    ...rest
  } = props;

  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error }
  } = useController({
    control,
    defaultValue,
    name: name as Path<T>,
    rules: rules as UseControllerProps<T>['rules']
  });

  return (
    <Input
      {...rest}
      error={hideErrors ? undefined : error?.message}
      id={name}
      maxLength={maxLength}
      onBlur={onBlur}
      onChange={
        !onChangeNormalizer
          ? onChange
          : event => onChangeNormalizer(event as ChangeEvent<HTMLInputElement>, onChange, value)
      }
      ref={assignRefs(forwardedRef, ref)}
      type={type ?? 'text'}
      value={value}
    />
  );
}
